import { useState, useEffect } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useSessionAuth } from "@/app/_utils/sessionClient";
import { PERMISSIONS } from "@/app/_utils/permissions";
import { Show } from "@/typings";
import ShowService from "@/app/_services/ShowService";
import PageContainer from "@/app/admin/_common/PageContainer";
import ShowDetailHeader from "./_cmp/ShowDetailHeader";

const TAB_PERMISSIONS: Record<string, string> = {
  relatedshows: PERMISSIONS.SHOW_WRITE,
  details: PERMISSIONS.PAGE_SHOWS,
  live: PERMISSIONS.PAGE_SHOWS,
  lineup: PERMISSIONS.PAGE_SHOWS,
  expenses: PERMISSIONS.FINANCE_PAGE,
  guestlist: PERMISSIONS.PAGE_SHOWS,
  masterguestlist: PERMISSIONS.PAGE_SHOWS,
  intakelist: PERMISSIONS.PAGE_SHOWS,
  checkouts: PERMISSIONS.SHOW_WRITE,
  checklist: PERMISSIONS.PAGE_SHOWS,
  settings: PERMISSIONS.SHOW_WRITE,
};

const INVALID_SHOW_IDS = new Set(["undefined", "null"]);

export default function DashLayout() {
  const { show_id } = useParams<{ show_id: string }>();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { session, can } = useSessionAuth();
  const [showDetails, setShowDetails] = useState<Show>();
  const isValidShowId = Boolean(show_id) && !INVALID_SHOW_IDS.has(show_id ?? "");
  const fullBleed = pathname.endsWith("/checklist");

  useEffect(() => {
    if (!isValidShowId) {
      navigate("/admin/shows", { replace: true });
      return;
    }
    ShowService.getShow({ id: show_id }).then(setShowDetails);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show_id, isValidShowId]);

  useEffect(() => {
    if (!session || !show_id) return;
    const currentTab = pathname.split("/").slice(-1)[0];
    const needed = TAB_PERMISSIONS[currentTab];
    if (needed && !can(needed)) {
      navigate(`/admin/shows/${show_id}/guestlist`);
    }
  }, [pathname, session, can, show_id, navigate]);

  return (
    <PageContainer flush>
      <div className="flex h-screen max-h-screen w-full flex-col overflow-hidden">
        {showDetails && (
          <ShowDetailHeader showDetails={showDetails} />
        )}
        {/* Checklist runs its own two scroll panes edge to edge, so it skips the padded scroller. */}
        <div
          className={
            fullBleed
              ? "flex min-h-0 flex-1 flex-col overflow-hidden"
              : "min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-5 md:px-5"
          }
        >
          {showDetails && <Outlet context={showDetails} />}
        </div>
      </div>
    </PageContainer>
  );
}
